import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeanteData } from "../../../../Universal/API/Context-API/AppProvider";
import {
  CatalogTypeItem,
  FilterConfigItem,
  FilterKey,
  FiltersState,
} from "../../../types";
import { collectUniqueValues, decodeDeep } from "../../../utils";

const EMPTY_FILTERS: FiltersState = {
  colors: [],
  types: [],
  prims: [],
  collections: [],
};

export const useCatalogFilterState = ({
  catalogTypes,
  allUnitData,
}: {
  catalogTypes: CatalogTypeItem[] | undefined;
  allUnitData: Record<string, DeanteData> | null;
}) => {
  const [selectedTypeIndex, setSelectedTypeIndex] = useState<number | null>(null);
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);

  const selectedType: CatalogTypeItem | null = useMemo(() => {
    if (selectedTypeIndex == null) return null;
    if (!catalogTypes || !catalogTypes[selectedTypeIndex]) return null;
    return catalogTypes[selectedTypeIndex];
  }, [catalogTypes, selectedTypeIndex]);

  const normalize = useCallback((value: string) => value.trim().toLowerCase(), []);

  const matchesColors = useCallback(
    (unit: DeanteData, selected: string[]) => {
      if (!selected.length) return true;
      const colors = [unit.pal]
        .filter(Boolean)
        .map((color) => String(color).toLowerCase());
      return selected.some((color) => colors.includes(normalize(color)));
    },
    [normalize]
  );

  const matchesTypes = useCallback(
    (unit: DeanteData, selected: string[]) => {
      if (!selected.length) return true;
      const type = normalize(String((unit as any).type ?? ""));
      return selected.some((value) => normalize(value) === type);
    },
    [normalize]
  );

  const matchesPrims = useCallback(
    (unit: DeanteData, selected: string[]) => {
      if (!selected.length) return true;
      const prim = normalize(String((unit as any).prim ?? ""));
      return selected.some((value) => normalize(value) === prim);
    },
    [normalize]
  );

  const matchesCollections = useCallback(
    (unit: DeanteData, selected: string[]) => {
      if (!selected.length) return true;
      const collection = normalize(String((unit as any).col ?? ""));
      return selected.some((value) => normalize(value) === collection);
    },
    [normalize]
  );

  const applyFiltersExcept = useCallback(
    (units: DeanteData[], exceptKey: FilterKey | null) => {
      let result = units;

      if (exceptKey !== "colors") {
        result = result.filter((unit) => matchesColors(unit, filters.colors ?? []));
      }
      if (exceptKey !== "types") {
        result = result.filter((unit) => matchesTypes(unit, filters.types ?? []));
      }
      if (exceptKey !== "prims") {
        result = result.filter((unit) => matchesPrims(unit, filters.prims ?? []));
      }
      if (exceptKey !== "collections") {
        result = result.filter((unit) =>
          matchesCollections(unit, filters.collections ?? [])
        );
      }

      return result;
    },
    [filters, matchesCollections, matchesColors, matchesPrims, matchesTypes]
  );

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
  }, []);

  const toggleFilterValue = useCallback((key: FilterKey, value: string) => {
    setFilters((prev) => {
      const current = prev[key] ?? [];
      const exists = current.some(
        (currentValue) => currentValue.toLowerCase() === value.toLowerCase()
      );

      let nextValues: string[] = [];

      if (key === "prims" || key === "collections") {
        nextValues = exists ? [] : [value];
      } else {
        nextValues = exists
          ? current.filter((currentValue) => currentValue.toLowerCase() !== value.toLowerCase())
          : [...current, value];
      }

      if (key === "prims") {
        return {
          ...prev,
          prims: nextValues,
          types: [],
        };
      }

      return { ...prev, [key]: nextValues };
    });
  }, []);

  const allUnitsDataForType = useMemo<DeanteData[]>(() => {
    if (!selectedType || !allUnitData) return [];
    const selectedTypeName = String((selectedType as any).type ?? "").toLowerCase();

    return Object.values(allUnitData).filter(
      (unit) => String((unit as any).superType || "").toLowerCase() === selectedTypeName
    );
  }, [selectedType, allUnitData]);

  const availableColors = useMemo(() => {
    const units = applyFiltersExcept(allUnitsDataForType, "colors");
    return collectUniqueValues(units, (unit) => [unit.pal]);
  }, [allUnitsDataForType, applyFiltersExcept]);

  const availableTypes = useMemo(() => {
    const units = applyFiltersExcept(allUnitsDataForType, "types");
    return collectUniqueValues(units, (unit) => [(unit as any).type]);
  }, [allUnitsDataForType, applyFiltersExcept]);

  const allColors = useMemo(
    () => collectUniqueValues(allUnitsDataForType, (unit) => [unit.pal]),
    [allUnitsDataForType]
  );

  const allTypes = useMemo(
    () => collectUniqueValues(allUnitsDataForType, (unit) => [(unit as any).type]),
    [allUnitsDataForType]
  );

  const availablePrims = useMemo(
    () => collectUniqueValues(allUnitsDataForType, (unit) => [(unit as any).prim]),
    [allUnitsDataForType]
  );

  const availableCollections = useMemo(() => {
    const units = applyFiltersExcept(allUnitsDataForType, "collections");
    return collectUniqueValues(units, (unit) => [(unit as any).col]);
  }, [allUnitsDataForType, applyFiltersExcept]);

  const allCollections = useMemo(
    () => collectUniqueValues(allUnitsDataForType, (unit) => [(unit as any).col]),
    [allUnitsDataForType]
  );

  const filterConfig = useMemo<FilterConfigItem[]>(
    () => [
      { key: "prims", title: "Вид:", options: availablePrims },
      {
        key: "types",
        title: "Тип:",
        options: allTypes,
        unavailableOptions: allTypes.filter(
          (value) =>
            !availableTypes.some(
              (available) => available.toLowerCase() === value.toLowerCase()
            )
        ),
      },
      {
        key: "colors",
        title: "Цвет:",
        options: allColors,
        unavailableOptions: allColors.filter(
          (value) =>
            !availableColors.some(
              (available) => available.toLowerCase() === value.toLowerCase()
            )
        ),
      },
      {
        key: "collections",
        title: "Коллекция:",
        options: allCollections,
        // presentation: "dropdown",
        // inlineLabel: true,
        // placeholder: "Выберите коллекцию",
        // clearLabel: "Все коллекции",
        unavailableOptions: allCollections.filter(
          (value) =>
            !availableCollections.some(
              (available) => available.toLowerCase() === value.toLowerCase()
            )
        ),
      },
    ],
    [
      allCollections,
      allColors,
      allTypes,
      availableCollections,
      availableColors,
      availablePrims,
      availableTypes,
    ]
  );

  useEffect(() => {
    if (allUnitsDataForType.length === 0) return;

    const allowedByKey: Record<FilterKey, Set<string>> = {
      colors: new Set(availableColors.map((value) => value.toLowerCase())),
      types: new Set(availableTypes.map((value) => value.toLowerCase())),
      prims: new Set(availablePrims.map((value) => value.toLowerCase())),
      collections: new Set(availableCollections.map((value) => value.toLowerCase())),
    };

    setFilters((prev) => {
      let changed = false;
      const next: FiltersState = { ...prev };

      (Object.keys(prev) as FilterKey[]).forEach((key) => {
        const allowed = allowedByKey[key];
        const cleaned = (prev[key] ?? []).filter((value) => allowed.has(value.toLowerCase()));
        if (cleaned.length !== (prev[key] ?? []).length) changed = true;
        next[key] = cleaned;
      });

      return changed ? next : prev;
    });
  }, [
    availableCollections,
    availableColors,
    availablePrims,
    availableTypes,
    allUnitsDataForType.length,
  ]);

  return {
    selectedTypeIndex,
    setSelectedTypeIndex,
    filters,
    setFilters,
    selectedType,
    clearFilters,
    toggleFilterValue,
    allUnitsDataForType,
    availableColors,
    availableTypes,
    availablePrims,
    availableCollections,
    filterConfig,
  };
};

export const useCatalogUrlState = ({
  catalogTypes,
  selectedTypeIndex,
  setSelectedTypeIndex,
  filters,
  setFilters,
  carouselRef,
  setActiveSlide,
  setSlide2Ready,
  isFocused,
  isLeavingRef,
  availableColors,
  availableTypes,
  availablePrims,
  availableCollections,
}: {
  catalogTypes: CatalogTypeItem[] | undefined;
  selectedTypeIndex: number | null;
  setSelectedTypeIndex: React.Dispatch<React.SetStateAction<number | null>>;
  filters: FiltersState;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  carouselRef: React.RefObject<any>;
  setActiveSlide: React.Dispatch<React.SetStateAction<number>>;
  setSlide2Ready: React.Dispatch<React.SetStateAction<boolean>>;
  isFocused: boolean;
  isLeavingRef: React.MutableRefObject<boolean>;
  availableColors: string[];
  availableTypes: string[];
  availablePrims: string[];
  availableCollections: string[];
}) => {
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);
  const openFiltersAfterInitRef = useRef(false);

  useEffect(() => {
    if (initializedFromUrl) return;
    if (!catalogTypes || catalogTypes.length === 0) return;
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    const rawTypeParam = params.get("type");
    const rawColorsParam = params.get("colors");
    const rawPrimsParam = params.get("prims");
    const rawTypesParam = params.get("types");
    const rawCollectionsParam = params.get("collections");

    let nextTypeIndex: number | null = null;
    let nextColorsRaw: string[] = [];
    let nextTypesRaw: string[] = [];
    let nextPrimsRaw: string[] = [];
    let nextCollectionsRaw: string[] = [];

    if (rawTypeParam) {
      const decodedType = decodeDeep(rawTypeParam);
      const index = catalogTypes.findIndex((typeItem) => typeItem.type === decodedType);

      if (index !== -1) {
        nextTypeIndex = index;

        if (rawColorsParam) {
          nextColorsRaw = decodeDeep(rawColorsParam)
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
        }

        if (rawTypesParam) {
          nextTypesRaw = decodeDeep(rawTypesParam)
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
        }

        if (rawPrimsParam) {
          nextPrimsRaw = decodeDeep(rawPrimsParam)
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
        }

        if (rawCollectionsParam) {
          nextCollectionsRaw = decodeDeep(rawCollectionsParam)
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
        }

        openFiltersAfterInitRef.current = true;
      }
    }

    setSelectedTypeIndex(nextTypeIndex);
    setFilters({
      colors: nextColorsRaw,
      types: nextTypesRaw,
      prims: nextPrimsRaw,
      collections: nextCollectionsRaw,
    });
    setInitializedFromUrl(true);
  }, [catalogTypes, initializedFromUrl, setFilters, setSelectedTypeIndex]);

  useEffect(() => {
    if (!openFiltersAfterInitRef.current) return;
    if (!carouselRef.current) return;

    carouselRef.current.scrollTo({ index: 1, animated: false });
    setActiveSlide(1);
    openFiltersAfterInitRef.current = false;

    let cancelled = false;
    const id = setTimeout(() => {
      if (!cancelled) setSlide2Ready(true);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [carouselRef, selectedTypeIndex, setActiveSlide, setSlide2Ready]);

  useEffect(() => {
    if (!initializedFromUrl) return;
    if (typeof window === "undefined") return;
    if (!isFocused) return;
    if (isLeavingRef.current) return;

    const id = window.setTimeout(() => {
      if (isLeavingRef.current) return;

      const url = new URL(window.location.href);
      const params = url.searchParams;

      const allowedColorsLower = new Set(availableColors.map((value) => value.toLowerCase()));
      const allowedTypesLower = new Set(availableTypes.map((value) => value.toLowerCase()));
      const allowedPrimsLower = new Set(availablePrims.map((value) => value.toLowerCase()));
      const allowedCollectionsLower = new Set(
        availableCollections.map((value) => value.toLowerCase())
      );

      const cleanColors = (filters.colors ?? []).filter((value) =>
        allowedColorsLower.has(value.toLowerCase())
      );
      const cleanTypes = (filters.types ?? []).filter((value) =>
        allowedTypesLower.has(value.toLowerCase())
      );
      const cleanPrims = (filters.prims ?? []).filter((value) =>
        allowedPrimsLower.has(value.toLowerCase())
      );
      const cleanCollections = (filters.collections ?? []).filter((value) =>
        allowedCollectionsLower.has(value.toLowerCase())
      );

      const nextColorsStr = cleanColors.length ? cleanColors.join(",") : null;
      const nextTypesStr = cleanTypes.length ? cleanTypes.join(",") : null;
      const nextPrimsStr = cleanPrims.length ? cleanPrims.join(",") : null;
      const nextCollectionsStr = cleanCollections.length
        ? cleanCollections.join(",")
        : null;
      const nextTypeStr =
        selectedTypeIndex != null && catalogTypes?.[selectedTypeIndex]
          ? String(catalogTypes[selectedTypeIndex].type ?? "")
          : null;

      let changed = false;

      const syncParam = (key: string, nextValue: string | null) => {
        const prevValue = params.get(key);
        if (nextValue == null) {
          if (prevValue !== null) {
            params.delete(key);
            changed = true;
          }
          return;
        }

        if (prevValue !== nextValue) {
          params.set(key, nextValue);
          changed = true;
        }
      };

      syncParam("type", nextTypeStr);
      syncParam("colors", nextColorsStr);
      syncParam("types", nextTypesStr);
      syncParam("prims", nextPrimsStr);
      syncParam("collections", nextCollectionsStr);

      if (changed) {
        url.search = params.toString();
        window.history.replaceState(null, "", url.toString());
      }
    }, 80);

    return () => window.clearTimeout(id);
  }, [
    availableCollections,
    availableColors,
    availablePrims,
    availableTypes,
    catalogTypes,
    filters,
    initializedFromUrl,
    isFocused,
    isLeavingRef,
    selectedTypeIndex,
  ]);
};
